import { useCallback, useEffect, useState } from 'react'
import {
  CalendarDays,
  RefreshCw,
  Download,
  TriangleAlert,
  FileText,
  Info,
} from 'lucide-react'
import {
  useGetStatementLines,
  useGetStatementPdfUrl,
  useRegenerateDraftStatement,
  useGetStatementRegenerationDeltas,
} from '../../hooks/backend/statements'
import { TooltipProvider } from '../../lib/shadcn/tooltip'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { useCapabilities } from '../../hooks/useCapabilities'
import {
  type StatementPeriod,
  type StatementLine,
  formatZar,
  formatDate,
} from '../../utils/statements'
import { OperationalStateChip } from './StateChips'
import { StatementLines } from './StatementLines'
import { StatementWrites } from './StatementWrites'
import { SendStatementDialog } from './SendStatementDialog'

function HeadlineFigure({ label, value }: { label: string; value: number }) {
  const negative = value < 0
  return (
    <div className="rounded-md border border-border bg-background px-3 py-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          'mt-0.5 text-sm font-semibold tabular-nums',
          negative ? 'text-rose-600 dark:text-rose-400' : 'text-foreground',
        )}
      >
        {formatZar(value)}
      </p>
    </div>
  )
}

// Regeneration deltas are only meaningful for issued statements: the view
// backing this panel filters to is_draft = false, so a draft period has
// nothing to compare against and should say so plainly instead of rendering a
// silently empty panel.
function RegenerationDeltaPanel({ vehicleId, periodStart }: { vehicleId: string; periodStart: string }) {
  const deltas = useGetStatementRegenerationDeltas()
  const trigger = deltas.trigger

  useEffect(() => {
    trigger({ vehicle_id: vehicleId, period_start: periodStart })
  }, [vehicleId, periodStart, trigger])

  const result = deltas.data as
    | { status: 'available'; rows: Record<string, unknown>[] }
    | { status: 'not_available'; reason: string }
    | undefined

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Info className="h-3.5 w-3.5" />
        Regeneration comparison
      </div>
      {deltas.loading ? (
        <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner className="h-3.5 w-3.5" /> Checking for differences...
        </div>
      ) : deltas.error ? (
        <p className="mt-2 text-sm text-destructive">{deltas.error}</p>
      ) : result?.status === 'not_available' ? (
        <p className="mt-2 text-sm text-muted-foreground">{result.reason}</p>
      ) : result?.status === 'available' && result.rows.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">
          No differences between the stored figures and what today's source data would produce.
        </p>
      ) : result?.status === 'available' ? (
        <pre className="mt-2 overflow-x-auto rounded-md bg-muted/40 p-2 text-xs text-foreground">
          {JSON.stringify(result.rows, null, 2)}
        </pre>
      ) : null}
    </div>
  )
}

export function StatementTab({
  periods,
  ownerName,
  loading,
  onPeriodsChanged,
}: {
  periods: StatementPeriod[]
  ownerName: string
  loading: boolean
  onPeriodsChanged: () => void
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const lines = useGetStatementLines()
  const pdf = useGetStatementPdfUrl()
  const regenerateDraft = useRegenerateDraftStatement()
  const { canEditOwners, isAdmin } = useCapabilities()
  const canEditStatements = canEditOwners || isAdmin
  const triggerLines = lines.trigger

  // Default to the newest period whenever the period list changes (vehicle switch).
  useEffect(() => {
    if (periods.length === 0) {
      setSelectedId(null)
      return
    }
    const stillPresent = periods.some((p) => p.statement_id === selectedId)
    if (!stillPresent) setSelectedId(periods[0]!.statement_id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods])

  useEffect(() => {
    if (selectedId) triggerLines({ statement_id: selectedId })
  }, [selectedId, triggerLines])

  const selected = periods.find((p) => p.statement_id === selectedId) ?? null

  // Draft periods can genuinely be recalculated from live source data: they
  // are not sent yet, so nothing is disturbed by refreshing them. Issued
  // periods keep the old, safe behaviour of re-rendering the stored snapshot,
  // never recalculating it, because that snapshot is what the owner already
  // received.
  const handleRegenerate = useCallback(async () => {
    if (!selected) return
    try {
      if (selected.is_draft) {
        await regenerateDraft.trigger({
          vehicle_id: selected.vehicle_id,
          period_start: selected.period_start,
        }).result
        onPeriodsChanged()
        await triggerLines({ statement_id: selected.statement_id }, { skipCache: true }).result
        toast.success('Draft statement regenerated', {
          description: 'Recalculated from live shift data. Figures may have changed.',
        })
      } else {
        await triggerLines({ statement_id: selected.statement_id }, { skipCache: true }).result
        toast.success('Statement re-rendered', {
          description: 'Rebuilt from the stored figures the owner already received.',
        })
      }
    } catch (err) {
      toast.error('Could not regenerate statement', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }, [selected, regenerateDraft, triggerLines, onPeriodsChanged])

  const handleDownload = useCallback(async () => {
    if (!selected) return
    if (!selected.statement_pdf_path) {
      toast.error('No PDF on file for this period')
      return
    }
    try {
      const res = await pdf.trigger({ statement_pdf_path: selected.statement_pdf_path }).result
      const url = (res as { url: string | null } | undefined)?.url ?? null
      if (!url) {
        toast.error('No PDF on file for this period')
        return
      }
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast.error('Could not open PDF', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }, [selected, pdf])

  const lineRows = (lines.data as StatementLine[] | undefined) ?? []
  const regenerating = selected?.is_draft ? regenerateDraft.loading : lines.loading

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Loading statements...
      </div>
    )
  }

  if (periods.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-sm text-muted-foreground">
        <FileText className="h-6 w-6" />
        No statements on file for this vehicle.
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-col gap-4 lg:flex-row">
      {/* Month picker */}
      <div className="lg:w-56 lg:shrink-0">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <CalendarDays className="h-3.5 w-3.5" />
          Periods
        </div>
        {/* Below lg: a single horizontal scrolling row of chips so the picker
            stays one line tall and does not push the statement off screen. */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
          {periods.map((p) => {
            const active = p.statement_id === selectedId
            return (
              <button
                key={p.statement_id}
                type="button"
                onClick={() => setSelectedId(p.statement_id)}
                className={cn(
                  'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  active
                    ? 'border-[hsl(var(--brand))]/40 bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                    : 'border-border bg-background text-muted-foreground hover:text-foreground',
                )}
              >
                <span>{p.period_label}</span>
                {p.is_draft && (
                  <span className="rounded-full bg-amber-100 px-1.5 py-0 text-[10px] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                    Draft
                  </span>
                )}
                {p.has_source_errors && (
                  <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                )}
              </button>
            )
          })}
        </div>

        {/* At lg and above: the existing vertical list inside the side column. */}
        <div className="hidden max-h-[70vh] overflow-y-auto rounded-lg border border-border lg:block">
          <ul>
            {periods.map((p) => {
              const active = p.statement_id === selectedId
              return (
                <li key={p.statement_id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(p.statement_id)}
                    className={cn(
                      'flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2 text-left text-sm transition-colors last:border-b-0',
                      active
                        ? 'bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                        : 'hover:bg-accent',
                    )}
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate font-medium">{p.period_label}</span>
                      {p.is_draft && (
                        <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0 text-[10px] font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                          Draft
                        </span>
                      )}
                    </span>
                    {p.has_source_errors && (
                      <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      </div>

      {/* Statement body */}
      <div className="min-w-0 flex-1 space-y-4">
        {selected && (
          <>
            {/* Header block */}
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold text-foreground">
                      {selected.plate}
                    </span>
                    <OperationalStateChip state={selected.operational_state} />
                    {selected.is_draft && (
                      <span className="rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                        Draft, not yet issued
                      </span>
                    )}
                  </div>
                  <h2 className="mt-1.5 text-base font-semibold text-foreground">
                    {selected.period_label}
                  </h2>
                  <p className="text-sm text-muted-foreground">{ownerName}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {canEditStatements && (
                    <button
                      type="button"
                      onClick={handleRegenerate}
                      disabled={regenerating}
                      title={
                        selected.is_draft
                          ? 'Recalculate this draft from live shift data'
                          : 'Re-render the stored figures the owner already received'
                      }
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50"
                    >
                      <RefreshCw className={cn('h-3.5 w-3.5', regenerating && 'animate-spin')} />
                      {selected.is_draft ? 'Regenerate draft' : 'Regenerate statement'}
                    </button>
                  )}
                  {canEditStatements && (
                    <SendStatementDialog
                      statementId={selected.statement_id}
                      disabled={!selected.owner_id}
                      disabledReason={
                        selected.owner_id
                          ? null
                          : 'This vehicle has no owner on file, so there is nowhere to send the statement.'
                      }
                    />
                  )}
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={pdf.loading || !selected.statement_pdf_path}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--brand))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--brand-foreground))] transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    {pdf.loading ? (
                      <Spinner className="h-3.5 w-3.5" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    Download PDF
                  </button>
                </div>
              </div>

              {/* Headline figures */}
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <HeadlineFigure label="Total earnings" value={selected.total_earnings_zar} />
                <HeadlineFigure label="Cost of sales" value={selected.total_cost_of_sales_zar} />
                <HeadlineFigure label="Operational costs" value={selected.total_operational_costs_zar} />
                <HeadlineFigure label="Net" value={selected.net_zar} />
              </div>

              {/* Provenance */}
              <p className="mt-3 text-[11px] text-muted-foreground">
                Source: {selected.source || 'n/a'}
                {selected.source_ref ? `, ${selected.source_ref}` : ''}
                {selected.generated_at ? `, generated ${formatDate(selected.generated_at)}` : ''}
              </p>
            </div>

            {/* Source-error banner */}
            {selected.has_source_errors && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                <span>This period contains source data errors, see the flagged lines below.</span>
              </div>
            )}

            {/* Regeneration comparison: issued periods only. Drafts get a plain
                explanation instead of a silently empty panel. */}
            {selected.is_draft ? (
              <div className="rounded-lg border border-border bg-card p-4">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <Info className="h-3.5 w-3.5" />
                  Regeneration comparison
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Draft periods have no comparison available yet: the comparison only covers issued
                  statements. Use Regenerate draft above to refresh this period's figures directly.
                </p>
              </div>
            ) : (
              <RegenerationDeltaPanel vehicleId={selected.vehicle_id} periodStart={selected.period_start} />
            )}

            {/* Lines */}
            {lines.error ? (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {lines.error}
              </div>
            ) : lines.loading && lineRows.length === 0 ? (
              <div className="flex items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" /> Rendering statement...
              </div>
            ) : (
              <TooltipProvider delayDuration={150}>
                <div className="overflow-hidden rounded-lg border border-border bg-card">
                  <StatementLines lines={lineRows} />
                  {/* Corrections found after issue. Issued statements only: a
                      draft has nothing to correct because its figures still move.
                      Renders nothing when there are none. */}
                  {!selected.is_draft && <StatementWrites statementId={selected.statement_id} />}
                </div>
              </TooltipProvider>
            )}
          </>
        )}
      </div>
    </div>
  )
}
